<template>
  <v-container fluid class="white page__container--padding py-5">
    <div class="d-flex align-center gap-2">
      <back-btn :to="{ name: 'CreditCustomersAdvance' }" />
      <div class="outlined-card p-1">
        <icon-title text="ANTICIPOS" icon="mdi-account-cash" />
      </div>
    </div>

    <!-- Data Customers -->
    <data-customer
      class="mt-5"
      :data-client="customerInfo"
      :loading="loading"
    />

    <!-- Buttons -->
    <v-row no-gutters class="mt-5">
      <v-col sm="12" md="4" class="card-shadow d-flex pa-1">
        <search-input
          v-model="search"
          :hide-details="true"
          :placeholder="'Buscar documento'"
          class="m-2 w-100"
        />
      </v-col>

      <v-col
        sm="12"
        md="8"
        class="card-shadow d-flex justify-center align-items-center pa-2"
      >
        <v-btn
          class="red-button rounded-pill button--small"
          :disabled="isButtonGenerateAdvanceDisabled"
          @click="openAdvanceApplication"
        >
          Generar NC de aplicación anticipo
        </v-btn>
        {{ totalNegativeAmount }}
      </v-col>
    </v-row>
    <!-- End Buttons -->

    <title-header-table>
      <span>Movimientos al {{ currentDate }}</span>
    </title-header-table>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :search="search"
      class="elevation-2 table__desing-general table__desing-border table__movements--scroll mb-5"
      :items-per-page="filteredItems.length"
      :mobile-breakpoint="null"
      :loading="loading"
      loading-text="Cargando... Por favor espere"
      :footer-props="footerProps"
      :options="dataTableOptions"
    >
      <template #[`header.TIPO`]="{ header }">
        {{ header.text }}
        <v-select
          v-model="showAllDocuments"
          :items="allDocuments"
          label="Todos los Documentos"
          dense
          solo
          hide-details
        />
      </template>

      <template #item="{ item }">
        <row-table-advance-payment
          :invoice="item"
          :selected="selectedInvoiceWell"
          @update:selected="selectInvoice"
          @actionButton="toggleInvoiceDetails(item)"
        />
      </template>
    </v-data-table>

    <confidentiality-notice />

    <!--- Modal Generate Advance -->
    <generate-application-advance
      v-model="showAdvanceApplicationModal"
      :selected-invoice-well="selectedInvoiceWell"
      :selected-invoice-reference="selectedInvoiceReference"
      :customer-code="$route.params.KUNNR"
      @generate="sendCreditNoteAndDPP"
    />
  </v-container>
</template>

<script>
  import ConfidentialityNotice from '@/components/ConfidentialityNotice';
  import { mapGetters } from 'vuex';
  import moment from 'moment';
  import axios from '@/plugins/axios';
  import SearchInput from '@/components/SearchInput.vue';
  import DataCustomer from '@/components/DataCustomer.vue';
  import TitleHeaderTable from '@/components/tables/TitleHeaderTable';
  import RowTableAdvancePayment from './components/RowTableAdvancePayment.vue';
  import IconTitle from '@/components/IconTitle.vue';
  import BackBtn from '@/components/BackBtn.vue';
  import GenerateApplicationAdvance from './components/GenerateApplicationAdvance.vue';
  import invoiceTableBehavior from '@/mixins/invoiceTableBehavior';

  export default {
    components: {
      ConfidentialityNotice,
      SearchInput,
      DataCustomer,
      TitleHeaderTable,
      RowTableAdvancePayment,
      IconTitle,
      BackBtn,
      GenerateApplicationAdvance,
    },
    mixins: [invoiceTableBehavior],
    data() {
      return {
        customerInfo: {},
        loadingCustomer: false,
        loadingInvoices: false,
        search: '',
        currentDate: moment().format('D [de] MMMM [del] YYYY HH:mm:ss a'),
        showAllDocuments: '',
        selectedInvoiceWell: {},
        selectedInvoiceReference: {},
        showAdvanceApplicationModal: false,
        headers: [
          {
            text: '# documento',
            value: 'BELNR',
            align: 'center',
            sortable: false,
          },
          {
            text: 'Orden de Compra',
            value: 'ORDCOM',
            align: 'center',
            sortable: false,
          },
          { text: 'Descargas', value: 'ver', align: 'center', sortable: false },
          {
            text: 'Tipo de documento',
            value: 'TIPO',
            align: 'center',
            sortable: false,
          },
          {
            text: 'Fecha de factura',
            value: 'BUDAT',
            align: 'center',
            sortable: false,
          },
          {
            text: 'Fecha de vencimiento',
            value: 'FVENC',
            align: 'center',
            sortable: false,
          },
          {
            text: 'Días vencido',
            value: 'DVENC',
            align: 'center',
            sortable: false,
          },
          { text: 'Monto', value: 'DMBTR', align: 'center', sortable: false },
          { text: '%DPP', value: 'DPP', align: 'center', sortable: false },
        ],
        dataTableOptions: {
          itemsPerPage: 15,
        },
        footerProps: {
          itemsPerPageOptions: [5, 10, 15, { text: 'Todos', value: -1 }],
          itemsPerPageText: 'Mostrar en grupo de:',
          showCurrentPage: false,
          pageText: '{0} al {1} de {2}',
        },
      };
    },
    computed: {
      filteredItems() {
        return this.items.filter(
          (item) =>
            !this.showAllDocuments || item.TIPO === this.showAllDocuments
        );
      },
      allDocuments() {
        const documentos = this.items.map((item) => ({
          text: item.TIPO,
          value: item.TIPO,
        }));
        return [{ text: 'Todos los Documentos', value: '' }, ...documentos];
      },
      isButtonGenerateAdvanceDisabled() {
        if (Object.keys(this.selectedInvoiceWell).length === 0) return true;

        /**
         * The credit note cannot be generated if the result of the subtraction is
         * within the tolerance range (-1 to 1)
         */
        const invoiceReference = this.selectedInvoiceReference;
        const totalNegativeAmount = invoiceReference.DETFAC.filter(
          (item) => item.TIPO === 'PAGO' || item.FKART === 'REF'
        ).reduce((sum, item) => {
          const amount = parseFloat(item.DMBTR);
          return amount < 0 ? sum + amount : sum;
        }, 0);

        const diferenceAmounts =
          totalNegativeAmount + parseFloat(invoiceReference.DMBTR || 0);
        return Math.abs(diferenceAmounts) > 1;
      },
      loading() {
        return this.loadingCustomer || this.loadingInvoices;
      },
      ...mapGetters({
        user: 'auth/user',
      }),
    },
    mounted() {
      this.getCustomerInfo();
      this.setInvoicesAdvancePayment();
    },
    methods: {
      async setInvoicesAdvancePayment() {
        this.loadingInvoices = true;
        try {
          const response = await axios.get('/credit/invoices-advance-payment', {
            params: {
              customer_code: this.$route.params.KUNNR,
              P_VTWEG: this.$route.params.VTWEG,
              P_VKORG: this.user.VKORG,
              P_OPCGPO: 0,
            },
          });

          this.items = this.reorderItemsData(response.data.data);
        } catch (error) {
          this.$toast.error('Ocurrió un error al obtener las facturas');
        } finally {
          this.loadingInvoices = false;
        }
      },
      async getCustomerInfo() {
        this.loadingCustomer = true;
        try {
          const response = await axios.post('grupo-cliente/data-client', {
            client: this.$route.params.KUNNR,
            VKORG: this.user.VKORG,
            VTWEG: this.$route.params.VTWEG,
            VKBUR: this.user.VKBUR,
            P_OPCGPO: this.opt != 'byGroup' ? 0 : '',
          });

          this.customerInfo = response.data.dataDetailClient;
        } catch (error) {
          this.$toast.error(
            'Ocurrió un error al obtener la información del cliente'
          );
        } finally {
          this.loadingCustomer = false;
        }
      },
      async sendCreditNoteAndDPP(params) {
        try {
          const { data } = await axios.post('/credit/credit-note-dpp', {
            P_FACTREF: params.invoiceReference,
            P_IMPTE: parseFloat(params.advaceApplicationAmount.toFixed(2)),
            P_FACTBIEN: params.invoiceWell,
            P_IMPTEPPD: parseFloat(
              params.earlyPaymentDiscountAmount.toFixed(2)
            ),
            P_KUNNR: this.$route.params.KUNNR,
            P_VTWEG: this.user.VTWEG,
          });

          if (data.data.MYERR == '0') {
            this.$toast.success('Nota de crédito generada exitósamente');
            this.setInvoicesAdvancePayment();
            this.selectedInvoiceWell = {};
          } else {
            this.$toast.error(data.data.FNMSG);
          }
        } catch (error) {
          this.$toast.error('Ocurrió un error al generar la nota de crédito');
        }

        this.showAdvanceApplicationModal = false;
      },
      selectInvoice(invoice) {
        this.selectedInvoiceWell = invoice;
        this.selectedInvoiceReference = this.items.find(
          (item) => item.BELNR === invoice.invoiceId
        );
      },
      openAdvanceApplication() {
        this.showAdvanceApplicationModal = true;
      },
    },
  };
</script>

<style scoped>
  ::v-deep table thead .v-input__slot,
  ::v-deep table thead .v-input__control {
    min-height: auto !important;
    background: transparent linear-gradient(180deg, #a82222 0%, #ff0000 100%) 0%
      0% no-repeat padding-box !important;
    opacity: 1;
  }
  ::v-deep table thead .v-input__slot .v-icon {
    color: #fff !important;
  }
  ::v-deep table .v-input__slot .v-label,
  ::v-deep table .v-input__slot .v-select__selection {
    font-size: 11px !important;
    color: #fff !important;
  }
</style>
